/**
 * Gera a lista de verificação administrativa em LaTeX
 *
 * Nota: o antigo item "C.4.1 - Apreciação de Riscos conforme ISO 12100" foi
 * removido e os demais foram renumerados. Como o código não é estável entre
 * versões, as respostas salvas são recuperadas pela descrição (estável),
 * caindo para o código atual apenas quando não houver correspondência. A
 * descrição exibida vem sempre da lista canônica, então itens antigos que não
 * existem mais (como o de ISO 12100) nunca são renderizados.
 */

import { escapeLatex } from '../core/latex-utils';
import { ItemVerificacao } from '@/types/lista-verificacao';

/**
 * Interface auxiliar para indexação da lista de verificação
 */
interface ListaVerificacaoDict {
  [key: string]: ItemVerificacao;
}

/**
 * Itens padrão da lista de verificação administrativa
 */
const ITENS_VERIFICACAO_PADRAO = [
  { codigo: 'C.4.1', descricao: 'Estudo de classificação de áreas IEC 60079' },
  { codigo: 'C.4.2', descricao: 'Manual de instruções de operação' },
  { codigo: 'C.4.3', descricao: 'Plano de manutenção preventiva ou corretiva' },
  { codigo: 'C.4.4', descricao: 'Plano de bloqueio de energias' },
  { codigo: 'C.4.5', descricao: 'Registro de treinamento para colaboradores' },
  { codigo: 'C.4.6', descricao: 'Diagrama elétrico' },
  { codigo: 'C.4.7', descricao: 'Diagrama hidráulico' },
  { codigo: 'C.4.8', descricao: 'Diagrama pneumático' },
  { codigo: 'C.4.9', descricao: 'LTCAT - Laudo Técnico das Condições Ambientais do Trabalho' },
  { codigo: 'C.4.10', descricao: 'AET - Análise Ergonômica do Trabalho' },
];

/**
 * Gera a seção C.4 - Lista de Verificação Administrativa da Máquina
 */
export function gerarListaVerificacaoAdministrativa(dados: any): string {
  const listaVerificacao: ListaVerificacaoDict = dados.listaVerificacaoAdministrativa || {};
  const valoresSalvos = Object.values(listaVerificacao);

  // Construir linhas da tabela
  const linhasTabela = ITENS_VERIFICACAO_PADRAO.map(item => {
    // A descrição é estável entre versões; o código não (houve remoção/renumeração).
    // Buscamos a resposta salva primeiro pela descrição e só caímos para o código
    // atual quando não houver correspondência. A descrição exibida é sempre a canônica.
    const itemDados = valoresSalvos.find(
      (v) => v && typeof v.descricao === 'string' && v.descricao === item.descricao
    ) || listaVerificacao[item.codigo];

    const possui = itemDados?.possui === true ? 'Sim' : itemDados?.possui === false ? 'Não' : 'N/I';
    const observacao = itemDados?.observacao || '';

    return `\\rowcolor{white}
${escapeLatex(item.codigo)} &
${escapeLatex(item.descricao)} &
${escapeLatex(possui)} &
${escapeLatex(observacao)} \\\\
\\hline`;
  }).join('\n');

  return `
\\subsection*{C.4. Lista de verificação administrativa da máquina}
\\addcontentsline{toc}{subsection}{C.4. Lista de verificação administrativa da máquina}
\\label{sec:lista-verificacao-administrativa}

\\begin{flushleft}
\\renewcommand{\\arraystretch}{1.5}
\\setlength{\\tabcolsep}{8pt}

\\begin{tabularx}{\\linewidth}{|>{\\raggedright\\arraybackslash}p{2cm}|>{\\raggedright\\arraybackslash}X|>{\\raggedright\\arraybackslash}p{2cm}|>{\\raggedright\\arraybackslash}X|}
\\hline
\\rowcolor{schmersalBlue}
\\textcolor{white}{\\textbf{Item}} &
\\textcolor{white}{\\textbf{Descrição}} &
\\textcolor{white}{\\textbf{Possui?}} &
\\textcolor{white}{\\textbf{Observação}} \\\\
\\hline
${linhasTabela}
\\end{tabularx}
\\end{flushleft}

`;
}

